import {
  RowData,
  RowDataReducerAction,
  SetupBaseRequestInfo,
  ToastInfo,
} from "../../../../../../types";
import { TOAST_MESSAGES } from "../../../../constants";
import {
  getInventoryBuildingIdentity,
  persistInventoryBuildingPriorities,
} from "./inventoryBuildingPriorityUtils";

const getOptimisticRowDataList = (
  rowDataList: RowData[],
  reorderedRows: RowData[],
): RowData[] => {
  const reorderedByIdentity = new Map(
    reorderedRows.map((record, index) => [
      getInventoryBuildingIdentity(record, index),
      record,
    ]),
  );

  return [...rowDataList]
    .map((record, index) => {
      const identity = getInventoryBuildingIdentity(record, index);
      return reorderedByIdentity.get(identity) ?? record;
    })
    .sort((a, b) => Number(a.priority ?? 0) - Number(b.priority ?? 0));
};

export async function reorderInventoryBuildingPriorities({
  rowDataList,
  reorderedRows,
  setupBaseRequestInfo,
  dispatch,
  setLandingPageToast,
  refetchRowDataList,
}: {
  rowDataList: RowData[];
  reorderedRows: RowData[];
  setupBaseRequestInfo: SetupBaseRequestInfo;
  dispatch: React.Dispatch<RowDataReducerAction>;
  setLandingPageToast: (toast: ToastInfo) => void;
  refetchRowDataList: () => Promise<{ error?: unknown }>;
}): Promise<void> {
  const previousRowDataList = rowDataList;
  const optimisticRowDataList = getOptimisticRowDataList(
    rowDataList,
    reorderedRows,
  );

  dispatch({
    type: "UPDATE_ROW_DATA_REDUCER_STATE",
    payload: {
      rowDataList: optimisticRowDataList,
    },
  });

  setLandingPageToast(TOAST_MESSAGES.SAVING());

  const result = await persistInventoryBuildingPriorities({
    records: reorderedRows,
    setupBaseRequestInfo,
  });

  if (!result.isSuccess) {
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        rowDataList: previousRowDataList,
      },
    });

    setLandingPageToast(
      TOAST_MESSAGES.INVENTORY_BUILDING_PRIORITIES_UPDATE_FAILED(),
    );
    return;
  }

  const refetchResult = await refetchRowDataList();
  if (refetchResult.error) {
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        rowDataList: previousRowDataList,
      },
    });

    setLandingPageToast(
      TOAST_MESSAGES.INVENTORY_BUILDING_PRIORITIES_UPDATE_FAILED(),
    );
    return;
  }

  setLandingPageToast(TOAST_MESSAGES.INVENTORY_BUILDING_PRIORITIES_UPDATED());
}
